/**
 * "Remember this device."
 *
 * What is remembered is deliberately small: the address last signed in with, and whether that
 * person uses a password or a PIN. No token, no name, nothing that identifies a shop — this is
 * a shop-counter tablet that customers stand in front of, and it survives a browser that anyone
 * can open.
 *
 * How LONG a session lasts is the server's business (the refresh cookie), not this. The value
 * of remembering the address is real all the same: a tailor signing in eight times a day now
 * types four digits instead of a whole address.
 *
 * The storage key is versioned because what it holds changed shape when identity moved from a
 * mobile number to an address — an old `tcrm.device` would otherwise pre-fill a phone number
 * into an email field forever.
 */

const STORAGE_KEY = 'tcrm.device.v2';

export type LoginMethod = 'password' | 'pin';

export interface RememberedDevice {
  email: string;
  method: LoginMethod;
}

function storage(): Storage | null {
  try {
    // Private browsing on iOS throws on access rather than returning null.
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readRememberedDevice(): RememberedDevice | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RememberedDevice>;
    if (typeof parsed.email !== 'string' || !parsed.email.includes('@')) return null;
    return { email: parsed.email, method: parsed.method === 'pin' ? 'pin' : 'password' };
  } catch {
    return null;
  }
}

export function rememberDevice(device: RememberedDevice): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(device));
  } catch {
    // A full or disabled store is not worth failing a sign-in over.
  }
}

export function forgetDevice(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    /* see above */
  }
}
